
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */


const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
  return <div {...props}/>
};

const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`We should only use visual material when it is complementary to the content in the webpages.`}</p>
    <h2>{`When to use what`}</h2>
    <p>{`Use different types of visuals in different contexts:`}</p>
    <p><em parentName="p">{`Photographs`}</em>{`: For dynamic content like news and people. Photographs can also be used in banners to communicate substance and emotions.`}</p>
    <p><em parentName="p">{`Illustrations`}</em>{`: For static content that is transformable into something visual, such as topics and themes. Provided they are informative, and descriptive.`}</p>
    <p><em parentName="p">{`Infographics`}</em>{`: Use these for visual storytelling to make complex information easier to understand. For example, an infographic could describe a workflow, how an investment plan works or the law-making process.`}</p>
    <h2>{`Best practices`}</h2>
    <ul>
      <li parentName="ul">{`your visual content is relevant to the topic of the webpage`}</li>
      <li parentName="ul">{`only add an image whenever it adds value to the webpage`}</li>
      <li parentName="ul">{`place images near the relevant text`}</li>
      <li parentName="ul">{`the most important image should be near the top of the webpage`}</li>
      <li parentName="ul">{`avoid embedding text in images, not all users can access them (page translation tools can't read images)`}</li>
      <li parentName="ul">{`text in HTML, provide alt text for images; follow rules of `}<a parentName="li" {...{
          "href": "https://webstyleguide.com/wsg3/11-graphics/8-web-graphics-markup.html#alt-text"
        }}>{`accessibility`}</a></li>
      <li parentName="ul">{`create good content is equally important as visual content for images`}</li>
    </ul>
    <h2>{`Widely supported image formats`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`File type (short)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`File type (long)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`File extension`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`GIF`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Graphics Interchange Format`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`.gif`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`JPEG`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Joint Photographic Expert Group image`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`.jpeg .jpg`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`PNG`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Portable Network Graphics`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`.png`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SVG`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Scalable Vector Graphics`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`.svg`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Raster graphic`}</h3>
    <p>{`Raster images are pixel-based. When you scale up a raster image you'll see jagged and blurry edges.`}</p>
    <h4>{`GIF (Graphics Interchange Format)`}</h4>
    <ul>
      <li parentName="ul">{`fewer colours, file size is smaller than JPEG`}</li>
      <li parentName="ul">{`interlaced progressive loading, low-quality version first, more detailed image is loaded next`}</li>
      <li parentName="ul">{`fewer colours, file size is smaller than JPEG`}</li>
      <li parentName="ul">{`does not lose any data with compression`}</li>
      <li parentName="ul">{`best use for web graphics with few colours only, and line drawings`}</li>
    </ul>
    <h4>{`JPG/JPEG (Joint Photographic-Experts-Group)`}</h4>
    <ul>
      <li parentName="ul">{`can display millions of colours, use JPEG format for photographs, still images, shading with light and dark`}</li>
      <li parentName="ul">{`use JPEG when file size is more important than the quality of the image`}</li>
      <li parentName="ul">{`optimising JPEG images; 60% - 75% is usually optimal for web publishing
16-bit data format`}</li>
      <li parentName="ul">{`compatible across many platforms and image editors`}</li>
    </ul>
    <h4>{`PNG (Portable Network Graphics)`}</h4>
    <ul>
      <li parentName="ul">{`support transparency (alpha channel), allow a translucent look`}</li>
      <li parentName="ul">{`lossless compression, no loss of data`}</li>
      <li parentName="ul">{`manage high-contrast or detailed images better than JPEG`}</li>
      <li parentName="ul">{`they can go down to very small file sizes when there are limited colours, use an 8-bit colour palette instead of a 24 colour palette`}</li>
      <li parentName="ul">{`PNG images can be used in any colour background still maintain the original appearance`}</li>
    </ul>
    <h3>{`Vector graphic`}</h3>
    <p>{`Vector image format can produce results with high fidelity at every resolution setting. It is an ideal format for high-resolution screens. If you zoom into a vector graphic it will always remain the same quality.`}</p>
    <h4>{`SVG (Scalable Vector Graphics)`}</h4>
    <ul>
      <li parentName="ul">{`SVG is in vector format – it does not lose any data when compressed`}</li>
      <li parentName="ul">{`SVG formats are supported by most contemporary browsers`}</li>
      <li parentName="ul">{`the image file size is lightweight and compressible
supports transparency`}</li>
      <li parentName="ul">{`icons used in ECL are uploaded in SVG format`}</li>
    </ul>
    <h2>{`Editing visuals`}</h2>
    <ul>
      <li parentName="ul">{`Don't apply effects, gradients, borders or filters. It can make the picture look manipulated.`}</li>
      <li parentName="ul">{`Don't pick photos with frames, rounded corners or drop shadows.`}</li>
      <li parentName="ul">{`Cropping. Pay attention to the copyright. In some cases you need prior consent from the author to alter the image.`}</li>
    </ul>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;